import React, { useEffect, useRef, useState } from "react";
import { Button, Form, InputGroup, Modal, Spinner, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import { deleteQuickReply, listQuickReplies, saveQuickReply } from "../../../services/whatsappChatApi";

// The saved WhatsApp replies. The Call and Chat Teams pick from them by typing "/" in the Chats reply box.

const FILL_INS = [
  { key: "customer_name", meaning: "The customer's name on WhatsApp. Left out when their name is only emoji or dots.", example: "Ramesh K" },
  { key: "rep_name", meaning: "Name of the employee sending the reply.", example: "Swetha" },
];
const KNOWN_KEYS = new Set(FILL_INS.map((fillIn) => fillIn.key));
const MAX_BODY = 4096;
const adminToken = () => localStorage.getItem("adminToken");
const problem = (error, fallback) => error?.response?.data?.detail || error?.response?.data?.message || fallback;

const unknownFillIns = (text) => {
  const keys = [...String(text || "").matchAll(/\{\{\s*([^{}]*?)\s*\}\}/g)].map((match) => match[1]);
  return [...new Set(keys.filter((key) => !KNOWN_KEYS.has(key.toLowerCase())))];
};

const changedOn = (value) => (value
  ? new Date(value).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true })
  : "-");

export default function QuickRepliesManager() {
  const [rows, setRows] = useState(null);
  const [failed, setFailed] = useState(false);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const bodyRef = useRef(null);

  const load = async () => {
    setFailed(false);
    try {
      setRows((await listQuickReplies("ADMIN", adminToken())) || []);
    } catch {
      setFailed(true);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const open = (row) => {
    setError("");
    setEditing({ id: row?.id, shortcut: row?.shortcut || "", body: row?.body || "" });
  };
  const change = (field, value) => {
    setError("");
    setEditing((old) => ({ ...old, [field]: value }));
  };

  // Puts the clicked fill-in where the cursor is, or at the end if the box was never focused.
  const insertFillIn = (key) => {
    const input = bodyRef.current;
    const token = `{{${key}}}`;
    const current = editing.body;
    const start = input ? input.selectionStart : current.length;
    const end = input ? input.selectionEnd : current.length;
    change("body", current.slice(0, start) + token + current.slice(end));
    requestAnimationFrame(() => {
      if (!input) return;
      input.focus();
      input.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await saveQuickReply(adminToken(), editing);
      await load();
      toast.success(editing.id ? "Quick reply updated" : "Quick reply added");
      setEditing(null);
    } catch (saveError) {
      // Stays in the box, so what was typed isn't lost.
      setError(problem(saveError, "Could not save the quick reply"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete /${row.shortcut}?\n\nThe teams will no longer see it. Messages already sent stay as they are. This cannot be undone.`)) return;
    setDeletingId(row.id);
    try {
      await deleteQuickReply(adminToken(), row.id);
      await load();
      toast.success("Quick reply deleted");
    } catch (deleteError) {
      toast.error(problem(deleteError, "Could not delete the quick reply"));
    } finally {
      setDeletingId(null);
    }
  };

  const unknown = unknownFillIns(editing?.body);
  const hideEditor = () => {
    if (!saving) setEditing(null);
  };

  return (
    <>
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div className="small text-muted">The Call and Chat Teams type / in the Chats reply box to pick one of these, then check it and send.</div>
        <Button onClick={() => open(null)}>New Quick Reply</Button>
      </div>
      <div className="table-responsive">
        <Table hover className="align-middle">
          <thead>
            <tr>
              <th style={{ width: 44 }}>#</th>
              <th>Shortcut</th>
              <th>Message</th>
              <th>Last Changed</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(rows || []).map((row, index) => (
              <tr key={row.id}>
                <td className="small fw-semibold">{index + 1}</td>
                <td className="fw-semibold text-nowrap">/{row.shortcut}</td>
                <td><div className="text-truncate" style={{ maxWidth: 480 }} title={row.body}>{row.body}</div></td>
                <td className="small text-nowrap">
                  <div>{changedOn(row.updatedAt)}</div>
                  {row.updatedBy && <div className="text-muted">{row.updatedBy}</div>}
                </td>
                <td className="text-end text-nowrap">
                  <Button size="sm" variant="outline-primary" className="me-2" onClick={() => open(row)} disabled={deletingId === row.id}>Edit</Button>
                  <Button size="sm" variant="outline-danger" onClick={() => remove(row)} disabled={deletingId === row.id}>
                    {deletingId === row.id ? <Spinner size="sm" /> : "Delete"}
                  </Button>
                </td>
              </tr>
            ))}
            {rows && !rows.length && <tr><td colSpan={5} className="text-center text-muted py-3">No quick replies yet.</td></tr>}
            {!rows && !failed && <tr><td colSpan={5} className="text-center text-muted py-3"><Spinner size="sm" className="me-2" />Loading quick replies...</td></tr>}
            {!rows && failed && (
              <tr><td colSpan={5} className="text-center text-muted py-3">
                Could not load the quick replies. <Button size="sm" variant="link" className="p-0 align-baseline" onClick={load}>Try again</Button>
              </td></tr>
            )}
          </tbody>
        </Table>
      </div>

      <Modal show={Boolean(editing)} onHide={hideEditor} centered size="lg">
        <Form onSubmit={save}>
          <Modal.Header closeButton><Modal.Title className="h6">{editing?.id ? "Edit Quick Reply" : "New Quick Reply"}</Modal.Title></Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3" style={{ maxWidth: 320 }} controlId="quick-reply-shortcut">
              <Form.Label className="small fw-semibold">Shortcut</Form.Label>
              <InputGroup>
                <InputGroup.Text>/</InputGroup.Text>
                <Form.Control
                  value={editing?.shortcut || ""}
                  onChange={(event) => change("shortcut", event.target.value)}
                  placeholder="price-coorg"
                  maxLength={33}
                  autoFocus
                />
              </InputGroup>
            </Form.Group>
            <div className="fw-semibold small mb-1">Fill-ins ({FILL_INS.length}) — click one to insert it</div>
            <Table size="sm" bordered className="small mb-3">
              <thead><tr><th>Fill-in</th><th>What it becomes</th><th>Example</th></tr></thead>
              <tbody>
                {FILL_INS.map((fillIn) => (
                  <tr key={fillIn.key}>
                    <td className="text-nowrap"><Button size="sm" variant="outline-primary" className="py-0" onClick={() => insertFillIn(fillIn.key)}>{`{{${fillIn.key}}}`}</Button></td>
                    <td>{fillIn.meaning}</td>
                    <td className="text-nowrap">{fillIn.example}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <Form.Group controlId="quick-reply-body">
              <Form.Label className="small fw-semibold">Message</Form.Label>
              <Form.Control
                ref={bodyRef}
                as="textarea"
                rows={8}
                maxLength={MAX_BODY}
                value={editing?.body || ""}
                onChange={(event) => change("body", event.target.value)}
              />
            </Form.Group>
            {unknown.length > 0 && (
              <div className="small text-danger mt-2">
                Not a fill-in: {unknown.map((key) => `{{${key}}}`).join(", ")}. The customer would see this text as it is. Check the spelling against the list above.
              </div>
            )}
            {error && <div className="small text-danger mt-2" role="alert">{error}</div>}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={hideEditor} disabled={saving}>Cancel</Button>
            <Button type="submit" disabled={saving}>{saving ? <Spinner size="sm" /> : "Save Quick Reply"}</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
}
